import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { LineSeries } from "@nivo/line";
import { useTheme } from "@mui/material/styles";
import { area, curveMonotoneX } from "d3-shape";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCompanyRatios } from "../hooks/useCompanyRatios";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialLineChart from "./shared/GenericFinancialLineChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";

dayjs.extend(quarterOfYear);

const MARGIN_CATEGORIES = [
  {
    key: "grossMargin",
    label: "Gross Margin",
    color: "#22c55e",
    field: "grossProfitMargin" as const,
  },
  {
    key: "operatingMargin",
    label: "Operating Margin",
    color: "#3b82f6",
    field: "operatingProfitMargin" as const,
  },
  {
    key: "netMargin",
    label: "Net Margin",
    color: "#f59e0b",
    field: "netProfitMargin" as const,
  },
];

const categoryColorMap: Record<string, string> = MARGIN_CATEGORIES.reduce(
  (acc, cat) => {
    acc[cat.label] = cat.color;
    return acc;
  },
  {} as Record<string, string>,
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GlowLayer = ({ series, innerHeight, innerWidth }: any) => {
  const instanceId = React.useId().replace(/:/g, "");

  if (!series || series.length === 0) return null;

  // Sort by average Y position — lowest Y = top of chart = highest margin value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortedSeries = [...series].sort((a: any, b: any) => {
    const avgA =
      a.data.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (sum: number, d: any) => sum + (d.position.y ?? 0),
        0,
      ) / a.data.length;
    const avgB =
      b.data.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (sum: number, d: any) => sum + (d.position.y ?? 0),
        0,
      ) / b.data.length;
    return avgA - avgB;
  });

  const clipId = `margins-glow-clip-${instanceId}`;
  const gradientId = (serieId: string | number) =>
    `margins-glow-${instanceId}-${String(serieId).replace(/\s+/g, "-")}`;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={innerWidth} height={innerHeight} />
        </clipPath>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {sortedSeries.map((serie: any) => (
          <linearGradient
            key={String(serie.id)}
            id={gradientId(serie.id)}
            x1="0"
            x2="0"
            y1="0"
            y2="1"
          >
            <stop offset="0%" stopColor={serie.color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={serie.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {sortedSeries.map((serie: any, serieIndex: number) => {
          const nextSerie = sortedSeries[serieIndex + 1];

          const points = serie.data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((d: any) => d.position.x != null && d.position.y != null)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((d: any) => ({
              x: d.position.x as number,
              y: d.position.y as number,
            }));

          if (points.length < 2) return null;

          // Build baseline: next series' line if it exists, otherwise chart bottom
          const baselineYMap = new Map<number, number>();
          if (nextSerie) {
            nextSerie.data
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .filter((d: any) => d.position.x != null && d.position.y != null)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .forEach((d: any) =>
                baselineYMap.set(d.position.x as number, d.position.y as number),
              );
          }

          const areaGen = area<{ x: number; y: number }>()
            .x((d) => d.x)
            .y1((d) => d.y)
            .y0((d) => baselineYMap.get(d.x) ?? innerHeight)
            .curve(curveMonotoneX);

          const path = areaGen(points);
          if (!path) return null;

          return (
            <path
              key={String(serie.id)}
              d={path}
              fill={`url(#${gradientId(serie.id)})`}
            />
          );
        })}
      </g>
    </g>
  );
};

interface MarginsChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const MarginsChart: React.FC<MarginsChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyRatios(
    ticker,
    maxLimit,
    period,
  );

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(MARGIN_CATEGORIES.map((cat) => cat.key)),
  );

  const chartData = useMemo((): LineSeries[] => {
    if (!data || data.length === 0) return [];

    const filteredData =
      effectiveViewMode === "annual"
        ? data.filter((item) => item.period === "FY")
        : data.filter((item) => ["Q1", "Q2", "Q3", "Q4"].includes(item.period));

    const sortedData = filteredData.sort(
      (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    );

    return MARGIN_CATEGORIES.filter((cat) =>
      visibleCategories.has(cat.key),
    ).map((category) => ({
      id: category.label,
      color: category.color,
      data: safeSlice(
        sortedData
          .filter((item) => item[category.field] != null)
          .map((item) => ({
            x:
              effectiveViewMode === "annual"
                ? dayjs(item.date).year().toString()
                : `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`,
            y: (item[category.field] ?? 0) * 100,
          })),
        effectiveLimit
      ),
    }));
  }, [data, visibleCategories, effectiveViewMode, effectiveLimit]);

  const isEmpty =
    !chartData ||
    chartData.length === 0 ||
    chartData.every((series) => series.data.length === 0);

  const toggleCategory = (categoryKey: string) => {
    setVisibleCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        if (newSet.size > 1) {
          newSet.delete(categoryKey);
        }
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const chipLegend = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mt: noHover ? 0 : 2,
        justifyContent: "center",
        px: noHover ? 0 : 2,
        ...(noHover && {
          maxWidth: "750px",
          mx: "auto",
        }),
      }}
    >
      {MARGIN_CATEGORIES.map((category) => (
        <ChartToggleButton
          key={category.key}
          label={category.label}
          selected={visibleCategories.has(category.key)}
          onToggle={() => toggleCategory(category.key)}
          color={category.color}
        />
      ))}
    </Box>
  );

  const tooltipRenderer = (point: {
    data: { xFormatted: string | number; y: number };
    serieId: string | number;
    serieColor: string;
  }) => {
    const rows = MARGIN_CATEGORIES.filter((cat) =>
      visibleCategories.has(cat.key),
    ).flatMap((category) => {
      const matchingPoint = chartData
        .find((s) => s.id === category.label)
        ?.data.find((d) => String(d.x) === String(point.data.xFormatted));
      if (!matchingPoint) return [];
      return [{ label: category.label, value: `${(matchingPoint.y as number)?.toFixed(1) ?? "0.0"}%`, color: category.color }];
    });

    return <ChartTooltip header={String(point.data.xFormatted)} rows={rows} />;
  };

  return (
    <GenericFinancialLineChart
      title="Profit Margins"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      isEmpty={isEmpty}
      enableCrosshair={false}
      lineWidth={3.5}
      customLayers={[GlowLayer]}
      bottomMargin={70}
      yAxisLegend="Margin (%)"
      yAxisFormat={(value) => `${Number(value).toFixed(0)}%`}
      colors={(serie: LineSeries) =>
        categoryColorMap[serie.id as string] ?? "#22c55e"
      }
      tooltip={tooltipRenderer}
      belowChartContent={chipLegend}
      noDataTitle="No Profit Margins Data Available"
      noDataDescription="Profit margins data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
    />
  );
};

export default MarginsChart;
